import React from 'react';
import {
  BoldSmallBodyText,
  Button,
  Card,
  Currency,
  Grid,
  ISelectOption,
  SmallBodyText,
  SpaceWrapper,
  SpruceHelpInlineLink,
} from 'ui-toolkit';
import styled from 'styled-components';
import { IOrderClosingProtectionLetterData } from 'types/order/documents/IOrderClosingProtectionLetterData';
import { TClosingProtectionLetterGenerationType } from 'API/order/documents/generateClosingProtectionLetterAsync';
import { eSelectorType, LenderSelector } from '../../../lenders/LenderSelector';
import { propertyAddressToString } from '../../../../ui-toolkit/orders/interfaces/IPropertyAddress';

const GridCell = styled(Grid)`
  grid-template-columns: 150px 1fr; //FYI: 150px is just wide enough for the largest label "Borrower CPL Cost", keep this number static so it's not "bouncy"
  border-bottom: ${(props) => props.theme.colors.defaultContentBorder} 1px solid;
  padding: ${(props) => props.theme.spacing.spacing4};
`;

export interface IClosingProtectionLetterCardProps {
  data: IOrderClosingProtectionLetterData;
  onGenerateCPL: (cplType: TClosingProtectionLetterGenerationType) => void;
  showBorrowerRow: boolean;
  lenderOptions: ISelectOption<string>[];
  updateOrderWithNewLender: (lenderId: string) => Promise<void>;
}

export const ClosingProtectionLetterCard: React.FC<IClosingProtectionLetterCardProps> = ({
  data,
  onGenerateCPL,
  showBorrowerRow,
  lenderOptions,
  updateOrderWithNewLender,
}) => {
  const anyCPLsExist = !!data.borrowerCPL || !!data.buyerCPL || !!data.lenderCPL || !!data.sellerCPL;
  const onChangeToState = (value: ISelectOption<string>) => {
    updateOrderWithNewLender(value.value);
    return value.value;
  };
  return (
    <SpaceWrapper>
      <Card
        title={`Closing Protection ${showBorrowerRow ? 'Letters' : 'Letter'}`}
        actions={
          <div style={{ textAlign: 'right' }}>
            {anyCPLsExist ? (
              <Grid templateColumns="max-content max-content">
                {data.borrowerCPL && (
                  <Button intent={'secondary'} type="button" onClick={() => onGenerateCPL('borrower')}>
                    Generate Borrower CPL
                  </Button>
                )}
                {data.lenderCPL && (
                  <Button intent={'secondary'} type="button" onClick={() => onGenerateCPL('lender')}>
                    Generate Lender CPL
                  </Button>
                )}
              </Grid>
            ) : (
              <SmallBodyText>
                American Digital has not processed this order yet, so CPL cannot be generated. Please{' '}
                <SpruceHelpInlineLink /> to submit a request for help.
              </SmallBodyText>
            )}
          </div>
        }
      >
        <Grid templateColumns="repeat(2, 1fr)">
          {/*ROW 1*/}
          <GridCell>
            <BoldSmallBodyText>Lender CPL Cost</BoldSmallBodyText>
            <SmallBodyText>
              {data.lenderCPL && <Currency value={data.lenderCPL.amountCents} valueIsCents={true} />}
            </SmallBodyText>
          </GridCell>
          <GridCell>
            <BoldSmallBodyText>Lender Name</BoldSmallBodyText>
            <SmallBodyText>
              <LenderSelector
                initLenderId={data.lender.id}
                lenderOptions={lenderOptions}
                selectorType={eSelectorType.dynamic}
                isFilterEnabled
                onUpdate={onChangeToState}
                isRequired={true}
                placeholder={`Select one...`}
              />
            </SmallBodyText>
          </GridCell>
          {/*ROW 2*/}
          <GridCell>
            <BoldSmallBodyText>
              {/*EMPTY ELEMENT TO FORCE SPACING IN LAYOUT*/}
              &nbsp;
            </BoldSmallBodyText>
          </GridCell>
          <GridCell>
            <BoldSmallBodyText>Lender Address</BoldSmallBodyText>
            <SmallBodyText>{propertyAddressToString(data.lender.address)}</SmallBodyText>
          </GridCell>
          {showBorrowerRow && (
            <>
              {/*ROW 3*/}
              <GridCell>
                <BoldSmallBodyText>Borrower CPL Cost</BoldSmallBodyText>
                <SmallBodyText>
                  {data.borrowerCPL && <Currency value={data.borrowerCPL.amountCents} valueIsCents={true} />}
                </SmallBodyText>
              </GridCell>
              <GridCell>
                <BoldSmallBodyText>Borrower Name</BoldSmallBodyText>
                <SmallBodyText>{data.borrowersNames}</SmallBodyText>
              </GridCell>
            </>
          )}
        </Grid>
      </Card>
    </SpaceWrapper>
  );
};
